import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from '@tanstack/react-query';
import { wishlistService } from '../services/api';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import ProductCard from '../components/ProductCard';
import DashboardSidebar from '../components/DashboardSidebar';
import { useAuth } from '../hooks/useAuth';
import { useWishlist } from '../hooks/useWishlist';

const extractResponseData = (payload) => payload?.data?.data ?? payload?.data ?? payload;

function WishlistNew() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const theme = getThemeVars(ACTIVE_THEME);
  const [toast, setToast] = useState(null);
  const [barVariant, setBarVariant] = useState(0);

  const VARIANT_NAMES = ['Glass Bold + Subtle Glass Cards', 'Glass Bold + Glass Cards', 'Mercado Libre', 'Current', 'Glass Bold Shadow', 'Glass Elevated'];
  const cardVariant = barVariant === 0 ? 'glass-subtle' : barVariant === 1 ? 'glass' : barVariant === 2 ? 'mercadolibre' : 'default';
  const shellBgOverride = barVariant === 2 ? '#f1f1f1' : null;
  const overlayRounding = cardVariant === 'mercadolibre' ? 'rounded-md' : 'rounded-lg';

  const {
    pendingProductIds,
    removeFromWishlist,
    refetchWishlist,
  } = useWishlist();

  const {
    data: wishlistPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isWishlistLoading,
    error: wishlistError,
    refetch: refetchCursor,
  } = useInfiniteQuery({
    queryKey: ['wishlist-cursor', user?.id],
    queryFn: async ({ pageParam }) => {
      const response = await wishlistService.getWishlistCursor({ pageSize: 16, cursor: pageParam });
      return extractResponseData(response);
    },
    getNextPageParam: (lastPage) => lastPage?.nextCursor ?? undefined,
    enabled: !!user,
  });

  const wishlistEntries = useMemo(() => {
    if (!wishlistPages?.pages) return [];
    return wishlistPages.pages.flatMap((page) =>
      (page?.items || []).map((product) => ({
        product,
        isAvailable: product?.status === 'AVAILABLE',
        hasActiveReservation: product?.status === 'RESERVED',
      }))
    );
  }, [wishlistPages]);

  const totalCount = wishlistEntries.length;

  // Infinite scroll observer
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current || !hasNextPage) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage(); },
      { rootMargin: '200px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (!user) navigate('/login');
  }, [navigate, user]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleWishlistToggle = async (product) => {
    try {
      await removeFromWishlist(product.id, product);
      setToast(t('Producto eliminado de tu lista de deseos'));
    } catch {
      setToast(t('Error al eliminar el producto'));
    }
  };

  return (
    <div
      className="dashboard-new font-body selection:bg-primary-container selection:text-on-primary-container h-[100dvh] overflow-hidden flex flex-col tablet-sm:flex-row"
      style={{ ...theme, backgroundColor: shellBgOverride || 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
    >
      <DashboardSidebar activePath="/wishlist" />
      <main className="flex-1 h-[100dvh] overflow-y-auto hide-scrollbar cm-pb-mobile-nav">
        <div className="px-6 tablet-sm:px-12 pt-8 pb-20 max-w-7xl mx-auto">
      <div className="mb-6 tablet-sm:mb-8">
        <h1 className="text-2xl tablet-sm:text-3xl font-black text-on-surface tracking-tight">{t('Mi Lista de Deseos')}</h1>
        <p className="text-on-surface-variant mt-1 text-sm tablet-sm:text-base">
          {totalCount} {totalCount === 1 ? t('producto guardado') : t('productos guardados')}
        </p>
      </div>

      {isWishlistLoading && (
        <div className="grid grid-cols-2 mobile-md:grid-cols-3 tablet-md:grid-cols-4 gap-3 mobile-sm:gap-4 tablet-sm:gap-6">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-surface-container rounded-xl overflow-hidden shadow-sm">
              <div className="aspect-square bg-surface-container-high animate-pulse" />
              <div className="p-3 space-y-2">
                <div className="h-4 bg-surface-container-high rounded animate-pulse w-3/4" />
                <div className="h-3 bg-surface-container-high rounded animate-pulse w-1/2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {wishlistError && !isWishlistLoading && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 p-4 rounded-lg bg-error-container text-on-error-container">
            <span className="material-symbols-outlined">error</span>
            {t('Error al cargar tu lista de deseos')}
          </div>
          <button onClick={refetchCursor} className="px-6 py-2.5 rounded-lg bg-primary text-on-primary font-medium hover:opacity-90 transition-opacity">
            {t('Reintentar')}
          </button>
        </div>
      )}

      {!isWishlistLoading && !wishlistError && wishlistEntries.length === 0 && (
        <div className="bg-surface border border-outline rounded-xl shadow-sm p-6 mobile-sm:p-10 tablet-sm:p-12 text-center">
          <span className="material-symbols-outlined text-[80px] text-on-surface-variant/40">bookmark</span>
          <h2 className="text-xl font-semibold text-on-surface mt-4">{t('Tu lista de deseos está vacía')}</h2>
          <p className="text-on-surface-variant mt-2">{t('Guarda productos que te interesen para verlos más tarde')}</p>
          <button onClick={() => navigate('/')} className="mt-6 w-full mobile-sm:w-auto px-8 py-3 rounded-xl bg-primary text-on-primary font-medium hover:opacity-90 transition-opacity">
            {t('Explorar Productos')}
          </button>
        </div>
      )}

      {!isWishlistLoading && !wishlistError && wishlistEntries.length > 0 && (
        <>
          <div className="grid grid-cols-2 mobile-md:grid-cols-3 tablet-md:grid-cols-4 gap-3 mobile-sm:gap-4 tablet-sm:gap-6">
            {wishlistEntries.map(({ product, isAvailable, hasActiveReservation }) => {
              const isRemoving = pendingProductIds[product.id];
              return (
                <div key={product.id} className={`relative ${isRemoving ? 'opacity-50 pointer-events-none' : ''}`}>
                  <ProductCard
                    product={product}
                    onClick={() => navigate(`/products/${product.id}`)}
                    wishlisted
                    onWishlistToggle={handleWishlistToggle}
                    variant={cardVariant}
                  />
                  {!isAvailable && (
                    <div className={`absolute inset-0 bg-black/40 ${overlayRounding} flex items-center justify-center pointer-events-none`}>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${hasActiveReservation ? 'bg-amber-500' : 'bg-red-500'}`}>
                        {hasActiveReservation ? t('Reservado') : t('No disponible')}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {isFetchingNextPage && (
            <div className="flex justify-center py-6">
              <div className="w-8 h-8 border-3 border-primary/30 border-t-primary rounded-full animate-spin" />
            </div>
          )}
          <div ref={sentinelRef} className="h-1" />
        </>
      )}

      {toast && (
        <div className="fixed dashboard-floating-bottom left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-lg bg-on-surface text-surface shadow-lg text-sm animate-fade-in">
          {toast}
        </div>
      )}
        </div>
      </main>

    </div>
  );
}

export default WishlistNew;
